import { motion } from "motion/react";
import {
  EXTRACTION_META,
  EXTRACTION_POLICY,
  EXTRACTION_SIGNALS,
  INTENTS,
  type Extraction,
  type ExtractionLevel,
  type IntentId,
} from "../../shared/types";

/** Compact tag for the second axis. Rendered on cards, stream rows, and panels. */
export function ExtractionTag({
  level,
  score,
  size = "sm",
}: {
  level: ExtractionLevel;
  score?: number;
  size?: "sm" | "md";
}) {
  const m = EXTRACTION_META[level];
  if (level === "clear") return null;
  const pad = size === "sm" ? "px-1.5 py-[2px] text-[9.5px]" : "px-2 py-[3px] text-[11px]";
  return (
    <span
      className={`mono inline-flex shrink-0 items-center gap-1 border-[1.5px] border-ink font-bold uppercase leading-none tracking-[0.08em] text-paper ${pad}`}
      style={{ background: m.accent }}
      title={`extraction ${m.label} — ${m.action}`}
    >
      ⧉ {m.label}
      {score !== undefined && <span className="tabular-nums opacity-80">{score.toFixed(2)}</span>}
    </span>
  );
}

function barColor(p: number): string {
  if (p >= EXTRACTION_POLICY.throttle) return "var(--magenta)";
  if (p >= EXTRACTION_POLICY.flag) return "#8b5cf6";
  return "var(--ink)";
}

/** The full second-axis readout: signals, composite, and the intent Choice. */
export function ExtractionPanel({ e, animate = true }: { e: Extraction; animate?: boolean }) {
  const m = EXTRACTION_META[e.level];
  const rows = [...EXTRACTION_SIGNALS].sort((a, b) => (e.nouls[b.id] ?? 0) - (e.nouls[a.id] ?? 0));
  const intents = (Object.keys(INTENTS) as IntentId[])
    .map((k) => ({ k, p: e.intentProbs[k] ?? 0 }))
    .sort((a, b) => b.p - a.p)
    .slice(0, 3);

  return (
    <div className="border-[1.5px] border-ink">
      <div
        className="flex items-center justify-between gap-2 px-2 py-1"
        style={{ background: e.level === "clear" ? "var(--grey)" : m.accent }}
      >
        <span
          className="mono text-[10px] font-bold uppercase tracking-[0.1em]"
          style={{ color: e.level === "clear" ? "var(--ink)" : "var(--paper)" }}
        >
          extraction · {m.label}
        </span>
        <span
          className="mono text-[10px] tabular-nums"
          style={{ color: e.level === "clear" ? "var(--ink)" : "var(--paper)", opacity: 0.85 }}
        >
          {e.score.toFixed(2)} → {m.action}
        </span>
      </div>

      <div className="flex flex-col gap-[5px] p-2">
        {rows.map((s, i) => {
          const p = e.nouls[s.id] ?? 0;
          const color = barColor(p);
          const hot = p >= EXTRACTION_POLICY.throttle;
          return (
            <div key={s.id} className="mono grid grid-cols-[125px_1fr_38px] items-center gap-2 text-[11px]">
              <span className="truncate lowercase" style={{ opacity: p >= EXTRACTION_POLICY.flag ? 1 : 0.5 }}>
                {s.id}
              </span>
              <div className="meter" style={{ color }}>
                <span
                  className="absolute inset-y-0 z-10 w-[1.5px] bg-ink opacity-30"
                  style={{ left: `${EXTRACTION_POLICY.flag * 100}%` }}
                />
                <span
                  className="absolute inset-y-0 z-10 w-[1.5px] bg-ink opacity-60"
                  style={{ left: `${EXTRACTION_POLICY.throttle * 100}%` }}
                />
                <motion.div
                  className={hot ? "meter-solid" : "meter-fill"}
                  style={{ background: hot ? color : undefined, color }}
                  initial={animate ? { width: 0 } : false}
                  animate={{ width: `${p * 100}%` }}
                  transition={{ duration: 0.45, delay: animate ? i * 0.04 : 0, ease: "easeOut" }}
                />
              </div>
              <span
                className="text-right tabular-nums"
                style={{ opacity: p >= EXTRACTION_POLICY.flag ? 1 : 0.45 }}
              >
                {p.toFixed(2)}
              </span>
            </div>
          );
        })}

        {/* Intent — a Choice, so it carries its own confidence */}
        <div className="mt-1 border-t-[1.5px] border-dashed border-ink/25 pt-2">
          <div className="mono mb-1.5 flex items-center justify-between text-[10px]">
            <span className="uppercase tracking-[0.1em] opacity-55">intent</span>
            <span className="tabular-nums opacity-55">conf {e.intentConfidence.toFixed(2)}</span>
          </div>
          {intents.map(({ k, p }) => (
            <div key={k} className="mono grid grid-cols-[125px_1fr_38px] items-center gap-2 text-[11px]">
              <span
                className="truncate lowercase"
                style={{ fontWeight: k === e.intent ? 700 : 400, opacity: k === e.intent ? 1 : 0.5 }}
              >
                {k}
              </span>
              <div className="meter" style={{ color: k === e.intent ? m.accent : "var(--ink)" }}>
                <motion.div
                  className={k === e.intent ? "meter-solid" : "meter-fill"}
                  style={{ background: k === e.intent ? m.accent : undefined }}
                  initial={animate ? { width: 0 } : false}
                  animate={{ width: `${p * 100}%` }}
                  transition={{ duration: 0.45, ease: "easeOut" }}
                />
              </div>
              <span className="text-right tabular-nums" style={{ opacity: k === e.intent ? 1 : 0.45 }}>
                {p.toFixed(2)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
